import { useEffect, useState, useCallback } from 'react';
import { getLeads, getUsers, downloadLeadsExport, takeLead } from '../../../services/api';
import CreateLeadModal from '../../../components/Leads/LeadsCreate/LeadsCreate';
import { 
  Plus, Loader2, Search, X, Zap, 
  RefreshCw, Calendar as CalendarIcon, AlertCircle, Download,
  Phone, User, FilterX
} from 'lucide-react';
import { LeadDetailsModal } from '../../../components/Leads/LeadDetailsModal/LeadDetailsModal';
import { CompleteActionModal } from '../../../components/Leads/CompleteActionModal/CompleteActionModal';

const LeadsBoard = () => {
  // Данные пользователя для проверки ролей
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userData.role === 'admin';

  const [viewMode, setViewMode] = useState('канбан'); 
  const [columns, setColumns] = useState({ new: [], processing: [], won: [], rejected: [] });
  const [rawLeads, setRawLeads] = useState([]); 
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportFormatLoading, setExportFormatLoading] = useState(null); // 'excel' | 'pdf' | null
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeadAction, setSelectedLeadAction] = useState(null);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleTakeLead = async (e, leadId) => {
    e.stopPropagation(); 
    
    try {
      await takeLead(leadId);
      fetchLeads(false, 1); 
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Не удалось взять лид';
      alert(errorMsg);
    }
  };
  const getLocalISODate = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(now - offset)).toISOString().split('T')[0];
    return localISOTime;
  };

  const [filters, setFilters] = useState({ 
    user_id: '', 
    status: 'all', 
    date: getLocalISODate(), 
    search: ''  
  });

  const kanbanColumnFor = (status) => {
    if (['warm', 'hot', 'customer'].includes(status)) return 'new';
    if (['new', 'processing', 'won', 'rejected'].includes(status)) return status;
    return 'new';
  };

  const statusMap = {
    new: { label: 'Новые', color: 'bg-indigo-500', badge: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
    processing: { label: 'В работе', color: 'bg-amber-500', badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    won: { label: 'Успешно', color: 'bg-emerald-500', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    rejected: { label: 'Отказ', color: 'bg-rose-500', badge: 'bg-rose-500/10 text-rose-400 border-rose-500/20' }
  };

  // Логика: если выбран статус, Канбан недоступен
  const isKanbanDisabled = filters.status !== 'all';

  const fetchLeads = useCallback(async (loadMore = false, forcePage = null) => {
    if (loadMore) setIsMoreLoading(true);
    else setLoading(true);

    const targetPage = forcePage || (loadMore ? page + 1 : 1);
    
    const params = { 
      page: targetPage,
      date: filters.date ? filters.date : undefined,
      search: filters.search ? filters.search : undefined,
      only_my: !isAdmin ? true : undefined // Для бэка
    };

    if (filters.status !== 'all') params.status = filters.status;
    if (isAdmin && filters.user_id) params.user_id = filters.user_id;

    try {
      const response = await getLeads(params);
      const newItems = response.items || [];

      setRawLeads(prev => {
        const updatedLeads = loadMore ? [...prev, ...newItems] : newItems;
        const grouped = updatedLeads.reduce((acc, item) => {
          const col = kanbanColumnFor(item.status);
          acc[col].push(item);
          return acc;
        }, { new: [], processing: [], won: [], rejected: [] });
        setColumns(grouped);
        return updatedLeads;
      });

      setMeta(response.meta);
      setPage(targetPage); 
    } catch (err) {
      console.error("Ошибка при загрузке сделок:", err);
    } finally {
      setLoading(false);
      setIsMoreLoading(false);
    }
  }, [filters, page, isAdmin]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); 
      fetchLeads(false, 1); 
    }, 400); 
    return () => clearTimeout(timer);
  }, [filters.search, filters.status, filters.date, filters.user_id]);

  useEffect(() => { 
    if (isAdmin) {
        getUsers().then(setManagers).catch(console.error); 
    }
  }, [isAdmin]);

  const handleStatusChange = (statusId) => {
    if (statusId === 'all') {
      setFilters(f => ({ ...f, status: 'all' }));
      setViewMode('канбан');
    } else {
      setFilters(f => ({ ...f, status: statusId }));
      setViewMode('таблица');
    }
  };

  const resetDateFilter = () => setFilters(f => ({ ...f, date: '' }));

  const getExportParams = () => {
    const p = {};
    if (filters.date) p.date = filters.date;
    if (filters.status && filters.status !== 'all') p.status = filters.status;
    if (filters.search) p.search = filters.search;
    if (isAdmin && filters.user_id) p.user_id = filters.user_id;
    return p;
  };

  const handleExport = async (format) => {
    setExportFormatLoading(format);
    try {
      await downloadLeadsExport({ ...getExportParams(), format });
    } catch (err) {
      let msg = err.response?.data?.message ?? err.message ?? 'Не удалось скачать отчёт';
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        console.error('Экспорт отчёта — ответ сервера:', text);
        try {
          const json = JSON.parse(text);
          msg = json.message || msg;
        } catch (_) {}
      } else {
        console.error('Экспорт отчёта:', err);
      }
      alert(typeof msg === 'string' ? msg : 'Не удалось скачать отчёт');
    } finally {
      setExportFormatLoading(null);
    }
  };

  const renderStatusBadge = (status) => {
    const current = statusMap[status] || statusMap.new;
    return (
      <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase border ${current.badge}`}>
        {current.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-3 sm:p-4 md:p-8 font-sans">
      <style>{`
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(0.8) sepia(100%) saturate(1000%) hue-rotate(190deg);
          cursor: pointer;
        }
        input[type="date"] { color-scheme: dark; }
      `}</style>

      <div className="max-w-[1800px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4 sm:mb-6 md:mb-10 gap-4 sm:gap-6">
          <div>
            <h1 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-black tracking-tighter text-white flex items-center gap-2 sm:gap-3 italic uppercase">Сделки</h1>
            <div className="flex items-center gap-2 mt-0.5 sm:mt-2 uppercase text-[8px] sm:text-[10px] font-black tracking-[0.2em] sm:tracking-[0.3em] text-indigo-400">
              <div className="h-1 w-6 bg-indigo-500 rounded-full" />
              <span className="sm:hidden">{filters.date === getLocalISODate() ? 'СЕГОДНЯ' : 'АРХИВ'}</span>
              <span className="hidden sm:inline">{filters.date === getLocalISODate() ? 'СЕГОДНЯШНИЕ ЗАПИСИ' : 'АРХИВ СДЕЛОК'}</span>
            </div>
          </div>
          
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="border-r flex gap-1.5 sm:gap-2 border-white/10 pr-2">
            <button 
    onClick={() => handleExport('excel')} 
    disabled={!!exportFormatLoading} 
    className="h-9 sm:h-[44px] md:h-[48px] bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 px-2.5 sm:px-5 rounded-xl sm:rounded-2xl font-black text-[8px] sm:text-[10px] uppercase flex items-center gap-1 sm:gap-2 hover:bg-emerald-600 hover:text-white transition-all disabled:opacity-50"
  >
    {exportFormatLoading === 'excel' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
    Excel
  </button>

  <button 
    onClick={() => handleExport('pdf')} 
    disabled={!!exportFormatLoading} 
    className="h-9 sm:h-[44px] md:h-[48px] bg-rose-600/10 text-rose-400 border border-rose-500/20 px-2.5 sm:px-5 rounded-xl sm:rounded-2xl font-black text-[8px] sm:text-[10px] uppercase flex items-center gap-1 sm:gap-2 hover:bg-rose-600 hover:text-white transition-all disabled:opacity-50"
  >
    {exportFormatLoading === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
    PDF
  </button>
        </div>
  
  {/* Переключатель Вида */}
  <div className="flex h-9 sm:h-[44px] md:h-[48px] bg-[#1e293b] p-1 rounded-xl sm:rounded-2xl border border-white/5 shadow-inner items-center">
    <button 
      disabled={isKanbanDisabled}
      onClick={() => setViewMode('канбан')} 
      className={`h-full px-2.5 sm:px-5 rounded-lg sm:rounded-xl text-[8px] sm:text-[10px] font-black transition-all flex items-center ${viewMode === 'канбан' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'} ${isKanbanDisabled ? 'opacity-20 cursor-not-allowed' : ''}`}
    >
      КАНБАН
    </button>
    <button 
      onClick={() => setViewMode('таблица')} 
      className={`h-full px-2.5 sm:px-5 rounded-lg sm:rounded-xl text-[8px] sm:text-[10px] font-black transition-all flex items-center ${viewMode === 'таблица' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
    >
      ТАБЛИЦА
    </button>
  </div>

  {/* Кнопка Новая сделка — только в адаптиве; на десктопе CreateLead не используется */}
  <button 
    onClick={() => setIsModalOpen(true)} 
    className="md:hidden h-9 sm:h-[44px] bg-white text-black hover:bg-indigo-600 hover:text-white px-2.5 sm:px-5 rounded-xl sm:rounded-2xl font-black text-[8px] sm:text-[10px] uppercase transition-all flex items-center gap-1 sm:gap-2 shadow-xl active:scale-95 whitespace-nowrap"
  >
    <Plus size={14} strokeWidth={3} className="sm:w-[18px] sm:h-[18px]" /> <span className="hidden sm:inline">НОВАЯ СДЕЛКА</span><span className="sm:hidden">СДЕЛКА</span>
  </button>
</div>
        </div>

        <div className="bg-[#1e293b]/30 p-3 sm:p-6 rounded-xl sm:rounded-[2.5rem] border border-white/5 shadow-2xl mb-3 sm:mb-6 md:mb-8">
  <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
    
    {/* Поиск */}
    <div className="relative flex-grow min-w-0 h-10 sm:h-[46px] order-1">
      <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={14} />
      <input 
        type="text" 
        placeholder="Сделка, клиент, телефон..." 
        value={filters.search} 
        onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} 
        className="bg-[#0f172a]/50 border border-white/10 rounded-xl sm:rounded-2xl pl-9 sm:pl-12 pr-8 sm:pr-10 h-full text-[10px] sm:text-xs text-slate-200 w-full focus:border-indigo-500 transition-all outline-none" 
      />
      {filters.search && (
        <X 
          size={14} 
          className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-slate-500 hover:text-white" 
          onClick={() => setFilters(f => ({...f, search: ''}))} 
        />
      )}
    </div>

    {/* Переключатель статусов — на мобильном горизонтальный скролл, короткие подписи */}
    <div className="flex overflow-x-auto overflow-y-hidden flex-nowrap sm:flex-wrap bg-[#0f172a]/50 p-1 rounded-xl sm:rounded-2xl border border-white/5 h-10 sm:h-[46px] items-center gap-0.5 order-2 min-w-0 custom-scrollbar">
      <button 
        onClick={() => handleStatusChange('all')} 
        className={`flex-shrink-0 px-2 sm:px-4 h-full rounded-lg sm:rounded-xl text-[7px] sm:text-[9px] font-black uppercase whitespace-nowrap transition-all ${filters.status === 'all' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
      >
        <span className="sm:hidden">Все</span><span className="hidden sm:inline">Все статусы</span>
      </button>
      {Object.entries(statusMap).map(([id, data]) => (
        <button 
          key={id} 
          onClick={() => handleStatusChange(id)} 
          className={`flex-shrink-0 px-2 sm:px-4 h-full rounded-lg sm:rounded-xl text-[7px] sm:text-[9px] font-black uppercase whitespace-nowrap transition-all ${filters.status === id ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
        >
          {data.label}
        </button>
      ))}
    </div>

    {/* Правая часть: Дата и Менеджеры — на мобильных опущено вниз с отступом */}
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 order-3 sm:ml-auto pt-3 sm:pt-0 mt-3 sm:mt-0 border-t border-white/10 sm:border-t-0 sm:border-none">
      
      {/* Дата */}
      <div className={`flex items-center gap-1.5 sm:gap-2 bg-[#0f172a]/50 border rounded-xl sm:rounded-2xl px-3 sm:px-4 h-10 sm:h-[46px] transition-all ${filters.date ? 'border-indigo-500/50' : 'border-white/10'}`}>
        <CalendarIcon size={12} className={filters.date ? 'text-indigo-400' : 'text-slate-500'} />
        <input 
          type="date" 
          value={filters.date} 
          onChange={(e) => setFilters(f => ({...f, date: e.target.value}))} 
          className="bg-transparent text-[8px] sm:text-[10px] font-black text-slate-300 outline-none uppercase cursor-pointer h-full max-w-[100px] sm:max-w-none" 
        />
        {filters.date && (
          <button onClick={resetDateFilter} className="text-slate-500 hover:text-rose-400">
            <FilterX size={12} />
          </button>
        )}
      </div>

      {/* Селект менеджеров — в адаптиве короткая подпись, чтобы не обрезалось */}
      {isAdmin && (
        <select 
          value={filters.user_id} 
          onChange={(e) => setFilters(f => ({...f, user_id: e.target.value}))} 
          title="Менеджер"
          className="bg-[#0f172a]/50 border border-white/10 rounded-xl sm:rounded-2xl px-2.5 sm:px-4 h-9 sm:h-[46px] text-[8px] sm:text-[10px] font-black text-slate-300 uppercase outline-none focus:border-indigo-500 cursor-pointer w-full sm:w-auto min-w-[90px] sm:min-w-[140px] md:min-w-[160px]"
        >
          <option value="">Все</option>
          {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      )}
    </div>

  </div>
</div>

        {loading ? (
          <div className="flex min-h-[400px] items-center justify-center rounded-[3rem] bg-[#1e293b]/20 border border-white/5">
            <Loader2 className="animate-spin text-indigo-500" size={40} />
          </div>
        ) : (
          viewMode === 'канбан' ? (
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 pb-6 md:pb-8 pt-4">
  {['new', 'processing', 'won', 'rejected'].map(key => (
    /* Убираем фиксированную ширину, ставим w-full и flex-1 */
    <div key={key} className="flex flex-col min-w-0 w-full">
      
      {/* Шапка колонки (теперь будет растягиваться вместе с колонкой) */}
      <div className="flex items-center justify-between mb-3 sm:mb-6 px-3 sm:px-5 py-1.5 sm:py-2 bg-white/5 rounded-xl sm:rounded-2xl border border-white/5 backdrop-blur-sm">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full shadow-[0_0_10px_rgba(0,0,0,0.5)] ${statusMap[key].color}`} />
          <span className="font-black text-white uppercase text-[9px] sm:text-[11px] tracking-[0.1em] sm:tracking-[0.15em]">{statusMap[key].label}</span>
        </div>
        <span className="text-[8px] sm:text-[10px] font-black text-slate-500 bg-[#0f172a] px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg border border-white/5">
          {(columns[key] || []).length}
        </span>
      </div>

      {/* Список карточек — в адаптиве меньше минимальная высота */}
      <div className="space-y-4 min-h-[120px] sm:min-h-[280px] lg:min-h-[500px]">
        {/* Замени цикл отрисовки карточек в Канбане на этот блок */}
{(columns[key] || []).map(lead => (
  <div 
    key={lead.id} 
    onClick={() => setSelectedLeadDetails(lead)} 
    className={`group relative w-full bg-gradient-to-b from-[#1e293b]/60 to-[#1e293b]/20 backdrop-blur-xl p-4 sm:p-6 rounded-xl sm:rounded-[2rem] border transition-all duration-300 cursor-pointer shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 ${
      !lead.user_id 
        ? 'border-indigo-500/40 shadow-[0_0_20px_rgba(99,102,241,0.1)]' 
        : 'border-white/10 hover:border-indigo-500/50'
    }`}
  >
    {/* Бейдж "Свободен", если нет менеджера */}
    {!lead.user_id && (
      <div className="absolute -top-2 right-12 bg-indigo-500 text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest shadow-lg z-20 animate-bounce">
        Свободен
      </div>
    )}

    {lead.is_overdue && (
      <div className="absolute -top-2 left-8 bg-rose-500 text-white text-[9px] font-black px-3 py-1 rounded-full uppercase flex items-center gap-1.5 shadow-[0_4px_12px_rgba(244,63,94,0.4)] z-20 animate-pulse">
        <AlertCircle size={12} strokeWidth={3} /> Просрочено
      </div>
    )}

    <button 
      onClick={(e) => { e.stopPropagation(); setSelectedLeadAction(lead); }} 
      className="absolute top-4 right-4 h-9 w-9 bg-white/5 hover:bg-indigo-500 text-slate-400 hover:text-white rounded-xl flex items-center justify-center transition-all duration-300 border border-white/10 group-hover:scale-110 shadow-lg"
    >
      <Zap size={16} fill="currentColor" className="opacity-50 group-hover:opacity-100" />
    </button>

    <div className="mb-3 sm:mb-5">
      <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
        <span className="text-[8px] sm:text-[10px] font-black text-indigo-400/80 bg-indigo-500/10 px-1.5 sm:px-2 py-0.5 rounded uppercase">#{lead.id}</span>
        <span className="text-[8px] sm:text-[10px] font-bold text-slate-500 italic">{new Date(lead.created_at).toLocaleDateString('ru-RU')}</span>
      </div>
      <h4 className="text-xs sm:text-[15px] font-bold text-slate-100 leading-snug group-hover:text-indigo-300 transition-colors">{lead.title}</h4>
    </div>

    {/* Секция действия (Кнопка "Взять" или информация о бюджете) */}
    <div className="flex flex-col gap-3 sm:gap-4 pt-3 sm:pt-5 border-t border-white/5">
      {!lead.user_id ? (
        <button 
          onClick={(e) => handleTakeLead(e, lead.id)}
          className="group/btn w-full h-9 sm:h-11 bg-indigo-600 hover:bg-indigo-500 text-white text-[9px] sm:text-[10px] font-black uppercase rounded-lg sm:rounded-xl transition-all flex items-center justify-center gap-1.5 sm:gap-2 shadow-lg shadow-indigo-900/20 active:scale-95"
        >
          <Plus size={12} className="sm:w-[14px] sm:h-[14px] group-hover/btn:rotate-90 transition-transform" strokeWidth={3} /> Взять этот лид
        </button>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[8px] sm:text-[9px] font-black text-slate-500 uppercase tracking-tighter mb-0.5">Бюджет</span>
            <span className="text-xs sm:text-[14px] font-black text-white tracking-tight">
              {Number(lead.price).toLocaleString()} <span className="text-indigo-500 text-[9px] sm:text-[11px]">₸</span>
            </span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-[8px] sm:text-[9px] font-black text-slate-500 uppercase tracking-tighter mb-0.5">Клиент</span>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-[9px] sm:text-[11px] font-bold text-slate-300 truncate max-w-[80px] sm:max-w-[100px]">{lead.client_name}</span>
              <div className="h-5 w-5 sm:h-6 sm:w-6 rounded-md sm:rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center text-[8px] sm:text-[10px] font-black text-indigo-400 border border-white/10">
                {lead.client_name?.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
))}
        
        {/* Заглушка */}
        {(columns[key] || []).length === 0 && (
          <div className="border-2 border-dashed border-white/5 rounded-xl sm:rounded-[2rem] h-24 sm:h-32 flex items-center justify-center w-full">
            <span className="text-[8px] sm:text-[10px] font-black text-slate-700 uppercase tracking-widest text-center px-4 sm:px-10">Пока нет сделок</span>
          </div>
        )}
      </div>
    </div>
  ))}
</div>
          ) : (
        <>
          {/* Мобильный вид: карточки (до md — таблица не помещается) */}
          <div className="md:hidden space-y-3 pb-2">
            {rawLeads.map((lead, index) => (
              <div
                key={lead.id}
                onClick={() => setSelectedLeadDetails(lead)}
                className={`rounded-xl border p-3 sm:p-4 active:scale-[0.99] transition-all cursor-pointer ${!lead.user_id ? 'bg-indigo-500/[0.06] border-white/10' : 'bg-[#1e293b]/60 border-white/5'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-black text-slate-500">#{(page - 1) * (meta?.per_page || 20) + index + 1}</span>
                      {!lead.user_id && (
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse flex-shrink-0" />
                      )}
                    </div>
                    <h3 className="text-xs font-bold text-slate-100 mt-0.5 truncate">{lead.title}</h3>
                    <p className="text-[9px] text-slate-500 uppercase font-black mt-0.5">{lead.client_name || 'Без имени'}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedLeadAction(lead); }}
                    className="p-2.5 rounded-lg bg-white/10 text-slate-400 flex-shrink-0"
                    title="Действие"
                  >
                    <Zap size={16} fill="currentColor" />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-white/5">
                  <span className="text-[10px] font-black text-white bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                    {Number(lead.price).toLocaleString()} ₸
                  </span>
                  <span className="px-1.5 sm:px-2 py-0.5 rounded text-[8px] sm:text-[9px] font-black uppercase bg-white/10 text-slate-300 border border-white/10">
                    {(statusMap[lead.status] || statusMap.new).label}
                  </span>
                  <span className="text-[9px] font-bold text-slate-500">
                    {new Date(lead.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
                  </span>
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-[9px] font-bold text-slate-400 ml-auto">
                      <Phone size={10} /> {lead.phone}
                    </a>
                  )}
                </div>
                <div className="mt-3">
                  {lead.user_id ? (
                    <div className="flex items-center gap-1.5 text-[9px] sm:text-[11px] text-slate-400">
                      <User size={10} className="sm:w-3 sm:h-3" />
                      <span className="font-bold text-slate-300 truncate">{lead.manager?.name || '—'}</span>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => handleTakeLead(e, lead.id)}
                      className="w-full py-2 sm:py-2.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/30 text-indigo-300 text-[9px] sm:text-[10px] font-black uppercase flex items-center justify-center gap-1.5 sm:gap-2"
                    >
                      <Plus size={14} strokeWidth={3} /> Забрать
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Десктоп: таблица */}
          <div className="hidden md:block bg-[#1e293b]/40 backdrop-blur-md rounded-2xl md:rounded-[2.5rem] border border-white/5 overflow-hidden shadow-2xl overflow-x-auto">
  <table className="w-full text-left border-collapse min-w-[900px]">
    <thead className="bg-[#0f172a]/50 border-b border-white/5">
      <tr>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] w-14 md:w-16 text-center">№</th>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Сделка / Клиент</th>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Менеджер</th>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Контакт</th>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-center">Бюджет</th>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-center">Статус</th>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-center">Дата</th>
        <th className="p-4 md:p-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-right">Действие</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-white/[0.02]">
      {rawLeads.map((lead, index) => (
        <tr 
          key={lead.id} 
          onClick={() => setSelectedLeadDetails(lead)} 
          className={`group transition-all cursor-pointer ${!lead.user_id ? 'bg-indigo-500/[0.02] hover:bg-indigo-500/[0.05]' : 'hover:bg-white/[0.02]'}`}
        >
          <td className="p-4 md:p-6 text-center">
            <span className="text-[10px] font-black text-slate-600 group-hover:text-indigo-400 transition-colors">
              {(page - 1) * (meta?.per_page || 20) + index + 1}
            </span>
          </td>
          <td className="p-4 md:p-6">
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-200 group-hover:text-white truncate transition-colors">
                  {lead.title}
                </span>
                {!lead.user_id && (
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse shadow-[0_0_8px_rgba(99,102,241,0.6)]" />
                )}
              </div>
              <span className="text-[10px] text-slate-500 uppercase font-black mt-1 tracking-wider">
                {lead.client_name || 'Без имени'}
              </span>
            </div>
          </td>
          <td className="p-4 md:p-6">
            {lead.user_id ? (
              <div className="flex items-center gap-2.5">
                <div className="h-7 w-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-indigo-400 group-hover:border-indigo-500/30 transition-all">
                  <User size={14} />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[11px] font-bold text-slate-300 truncate">
                    {lead.manager?.name || '—'}
                  </span>
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Ответственный</span>
                </div>
              </div>
            ) : (
              <button 
                onClick={(e) => handleTakeLead(e, lead.id)}
                className="group/btn relative overflow-hidden px-4 py-2 bg-indigo-500/10 hover:bg-indigo-600 border border-indigo-500/20 hover:border-indigo-400 rounded-xl transition-all duration-300 shadow-lg shadow-indigo-900/20"
              >
                <div className="flex items-center gap-2 relative z-10 text-indigo-400 group-hover/btn:text-white transition-colors">
                  <Plus size={12} strokeWidth={3} className="group-hover/btn:rotate-90 transition-transform duration-300" />
                  <span className="text-[9px] font-black uppercase tracking-tighter">Забрать</span>
                </div>
              </button>
            )}
          </td>
          <td className="p-4 md:p-6">
            <a 
              href={`tel:${lead.phone}`} 
              onClick={(e) => e.stopPropagation()} 
              className="flex items-center gap-2 text-[11px] font-bold text-slate-300 hover:text-indigo-400 transition-colors"
            >
              <Phone size={10} className="text-slate-500" />
              {lead.phone || '—'}
            </a>
          </td>
          <td className="p-4 md:p-6 text-center">
            <span className="text-xs font-black text-white bg-white/5 px-3 py-1.5 rounded-lg border border-white/5">
              {Number(lead.price).toLocaleString()} <span className="text-indigo-500 text-[10px]">₸</span>
            </span>
          </td>
          <td className="p-4 md:p-6 text-center">
            {renderStatusBadge(lead.status)}
          </td>
          <td className="p-4 md:p-6 text-center whitespace-nowrap">
            <div className="flex flex-col items-center">
              <span className="text-[11px] font-bold text-slate-400 italic">
                {new Date(lead.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
              </span>
              <span className="text-[9px] font-medium text-slate-600 uppercase">
                {new Date(lead.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </td>
          <td className="p-4 md:p-6 text-right">
            <button 
              onClick={(e) => { e.stopPropagation(); setSelectedLeadAction(lead); }} 
              className="p-3 bg-indigo-500/10 text-indigo-400 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-lg active:scale-90"
              title="Быстрое действие"
            >
              <Zap size={16} fill="currentColor" />
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
        </>
          )
        )}

        {meta && meta.current_page < meta.last_page && (
          <div className="flex justify-center mt-12 pb-20">
            <button onClick={() => fetchLeads(true)} disabled={isMoreLoading} className="flex items-center gap-4 bg-[#1e293b] hover:bg-indigo-600 border border-white/5 px-10 py-5 rounded-[2rem] transition-all shadow-xl disabled:opacity-50">
              {isMoreLoading ? <Loader2 className="animate-spin text-indigo-400" size={24} /> : (
                <>
                  <RefreshCw size={20} className="text-indigo-400" />
                  <div className="text-left">
                    <p className="text-[10px] font-black uppercase tracking-widest text-white">Загрузить еще</p>
                    <p className="text-[8px] font-bold text-slate-500">Всего сделок: {meta.total}</p>
                  </div>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {!isDesktop && (
        <CreateLeadModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onRefresh={() => fetchLeads(false)} />
      )}
      {selectedLeadAction && (
        <CompleteActionModal isOpen={!!selectedLeadAction} lead={selectedLeadAction} onClose={() => setSelectedLeadAction(null)} onRefresh={() => fetchLeads(false)} />
      )}
      {selectedLeadDetails && (
        <LeadDetailsModal isOpen={!!selectedLeadDetails} lead={selectedLeadDetails} onClose={() => setSelectedLeadDetails(null)} onRefresh={() => fetchLeads(false)} />
      )}
    </div>
  );
};

export default LeadsBoard;